import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "@/store";
import { fetchAddresses, importAddresses, deleteAddress, createAddress, updateAddress, exportAddresses, importAddressesFile } from "@/store/slices/address.slice";
import {
  selectAllAddresses,
  selectAddressesLoading,
  selectAddressesError,
} from "@/store/selectors/address.selectors";
import { Address } from "@/types/address.types";
import { ExportOptions, ImportOptions, ImportResult, ExportResult } from "@/types/export.types";

export function useAddresses(): {
  addresses: Address[];
  loading: boolean;
  error: string | null;
  handleImport: (file: File) => Promise<{ successCount: number; failureCount: number }>;
  handleDelete: (id: string) => Promise<void>;
  handleCreate: (data: Omit<Address, "id" | "createdAt" | "updatedAt">) => Promise<void>;
  handleUpdate: (id: string, updates: Partial<Address>) => Promise<void>;
  handleExport: (options: ExportOptions) => Promise<ExportResult>;
  handleImportFile: (file: File, options: ImportOptions, onProgress?: (progress: number, total: number) => void) => Promise<ImportResult>;
} {
  const dispatch = useDispatch<AppDispatch>();
  const addresses = useSelector(selectAllAddresses);
  const loading = useSelector(selectAddressesLoading);
  const error = useSelector(selectAddressesError);

  useEffect(() => {
    void dispatch(fetchAddresses());
  }, [dispatch]);

  const handleImport = async (file: File): Promise<{ successCount: number; failureCount: number }> => {
    const result = await dispatch(importAddresses(file));
    if (importAddresses.fulfilled.match(result)) {
      return result.payload;
    }
    throw new Error('导入失败');
  };

  const handleDelete = async (id: string): Promise<void> => {
    await dispatch(deleteAddress(id));
  };

  const handleCreate = async (data: Omit<Address, "id" | "createdAt" | "updatedAt">): Promise<void> => {
    const result = await dispatch(createAddress(data));
    if (createAddress.rejected.match(result)) {
      throw new Error(result.payload as string);
    }
  };

  const handleUpdate = async (id: string, updates: Partial<Address>): Promise<void> => {
    const result = await dispatch(updateAddress({ id, updates }));
    if (updateAddress.rejected.match(result)) {
      throw new Error(result.payload as string);
    }
  };

  const handleExport = async (options: ExportOptions): Promise<ExportResult> => {
    const result = await dispatch(exportAddresses(options));
    if (exportAddresses.fulfilled.match(result)) {
      return result.payload;
    }
    throw new Error('导出失败');
  };

  const handleImportFile = async (
    file: File,
    options: ImportOptions,
    onProgress?: (progress: number, total: number) => void
  ): Promise<ImportResult> => {
    const result = await dispatch(importAddressesFile({ file, options, onProgress }));
    if (importAddressesFile.fulfilled.match(result)) {
      return result.payload;
    }
    throw new Error('导入失败');
  };

  return {
    addresses,
    loading,
    error,
    handleImport,
    handleDelete,
    handleCreate,
    handleUpdate,
    handleExport,
    handleImportFile,
  };
}
